import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface JobLogsModalProps {
  jobId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface JobData {
  eventID: string;
  raceID: string;
  participants: Array<{
    participantId: string;
    photo_to: number;
  }>;
  status: string;
  progress: number;
  log: string[];
  started_at: number;
  updated_at: number;
}

export const JobLogsModal = ({ jobId, isOpen, onClose }: JobLogsModalProps) => {
  console.log('JobLogsModal rendered with:', { jobId, isOpen });
  const [jobData, setJobData] = useState<JobData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchJobData = async () => {
    if (!jobId) {
      console.log('No jobId provided');
      return;
    }
    
    console.log('Fetching job data for:', jobId);
    setLoading(true);
    setError(null);
    
    try {
      const url = `https://api.equiphoto.ch/ingest/${jobId}`;
      console.log('Fetching from URL:', url);
      const response = await fetch(url);
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch job data: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Job data received:', data);
      setJobData(data);
    } catch (error) {
      console.error('Error fetching job data:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch job data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('useEffect triggered:', { isOpen, jobId });
    if (isOpen && jobId) {
      fetchJobData();
    }
  }, [isOpen, jobId]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'running':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'completed':
      case 'finished':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'running':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed':
      case 'finished':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'failed':
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Job Logs: {jobId}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                console.log('Refresh button clicked');
                fetchJobData();
              }}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Refresh'
              )}
            </Button>
          </DialogTitle>
        </DialogHeader>

        {loading && !jobData ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading job data...
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-8 text-red-600">
            <XCircle className="w-6 h-6 mr-2" />
            {error}
          </div>
        ) : jobData ? (
          <div className="space-y-6">
            {/* Job Status Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 border rounded">
                <div className="text-sm text-muted-foreground">Status</div>
                <Badge className={getStatusColor(jobData.status)}>
                  <div className="flex items-center gap-1">
                    {getStatusIcon(jobData.status)}
                    {jobData.status}
                  </div>
                </Badge>
              </div>
              <div className="text-center p-3 border rounded">
                <div className="text-sm text-muted-foreground">Progress</div>
                <div className="text-lg font-semibold">{jobData.progress}%</div>
              </div>
              <div className="text-center p-3 border rounded">
                <div className="text-sm text-muted-foreground">Participants</div>
                <div className="text-lg font-semibold">{jobData.participants.length}</div>
              </div>
              <div className="text-center p-3 border rounded">
                <div className="text-sm text-muted-foreground">Started</div>
                <div className="text-sm">{formatTimestamp(jobData.started_at)}</div>
              </div>
            </div>

            {/* Event and Race Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 border rounded">
                <div className="text-sm text-muted-foreground">Event ID</div>
                <div className="font-mono">{jobData.eventID}</div>
              </div>
              <div className="p-3 border rounded">
                <div className="text-sm text-muted-foreground">Race ID</div>
                <div className="font-mono">{jobData.raceID}</div>
              </div>
            </div>

            {/* Participants Summary */}
            <div className="space-y-2">
              <h3 className="font-semibold">Participants ({jobData.participants.length})</h3>
              <ScrollArea className="h-32 border rounded p-2">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-sm">
                  {jobData.participants.map((participant, index) => (
                    <div key={index} className="p-2 bg-muted rounded text-xs">
                      <div>ID: {participant.participantId}</div>
                      <div>Photos: {participant.photo_to}</div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Logs */}
            <div className="space-y-2">
              <h3 className="font-semibold">Logs ({jobData.log.length})</h3>
              <ScrollArea className="h-48 border rounded p-3">
                {jobData.log.length > 0 ? (
                  <div className="space-y-1 font-mono text-sm">
                    {jobData.log.map((logEntry, index) => (
                      <div key={index} className="text-muted-foreground">
                        {logEntry}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted-foreground text-center py-4">
                    No logs available yet
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Last Updated */}
            <div className="text-xs text-muted-foreground text-center">
              Last updated: {formatTimestamp(jobData.updated_at)}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};